import connectionsStore, { DragInfo3, PlaceID3 } from "@mobx/store/connections";
import { Point } from "framer-motion";
import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";

const emptyDrags: DragInfo3[] = [];
const AUTO_SCROLL_EDGE_PX = 120;
const AUTO_SCROLL_STEP_PX = 8;
const AUTO_SCROLL_INTERVAL_MS = 30;

const isScrollable = (el: HTMLElement) => {
  const style = window.getComputedStyle(el);
  const overflowY = style.overflowY;
  return ["auto", "scroll", "overlay"].includes(overflowY) && el.scrollHeight > el.clientHeight;
};

const findScrollParent = (el: HTMLElement | null) => {
  let current = el?.parentElement ?? null;
  while (current) {
    if (isScrollable(current)) return current;
    current = current.parentElement;
  }
  return document.scrollingElement as HTMLElement | null;
};

export const useDragWatch3 = (targetRef: MutableRefObject<HTMLElement | null>, placeID?: PlaceID3) => {
  const [hadDraggedElements, setHadDraggedElements] = useState(false);
  const [isReadyToDrag, setReadyToDrag] = useState(false);
  const onMouseMoveRef = useRef<null | ((e: MouseEvent) => void)>(null);
  const [pos, setPos] = useState<Point>({ x: 0, y: 0 });
  const [size, setSize] = useState<Point>({ x: 0, y: 0 });
  const [clones, setClones] = useState<HTMLElement[]>([]);
  const placeStore = useMemo(() => placeID ? connectionsStore.getPlaceStore(placeID) : undefined, [placeID]); 
  const dragStore = useMemo(() => placeStore ? placeStore.drag : undefined, [placeStore]);
  const [mousePos, setMousePos] = useState<Point>();
  const readyToDragRef = useRef(false);
  const mousePosRef = useRef<Point>();
  const dragElements = dragStore?.drags ?? emptyDrags;
  const isDrag = dragStore?.isDrag ?? false;

  const autoScroll = useCallback((cursorY?: number) => {
    if (cursorY === undefined || !targetRef.current) return;
    const scrollParent = findScrollParent(targetRef.current);
    if (!scrollParent) return;

    const isDocumentScroll = scrollParent === document.scrollingElement;
    const rect = isDocumentScroll
      ? { top: 0, bottom: window.innerHeight }
      : scrollParent.getBoundingClientRect();

    if (cursorY < rect.top + AUTO_SCROLL_EDGE_PX) {
      scrollParent.scrollBy({ top: -AUTO_SCROLL_STEP_PX, behavior: "auto" });
    } else if (cursorY > rect.bottom - AUTO_SCROLL_EDGE_PX) {
      scrollParent.scrollBy({ top: AUTO_SCROLL_STEP_PX, behavior: "auto" });
    }
  }, [targetRef]);

  useEffect(() => {
    readyToDragRef.current = isReadyToDrag;
    document.dispatchEvent(new CustomEvent("no-select", { detail: isReadyToDrag }));
    if (isReadyToDrag) {
      setTimeout(() => {
        if (readyToDragRef.current) dragStore?.setDrag(true);
      }, 400);
      let minLeft = 1e10;
      let minTop = 1e10;
      let maxWidth = 0;
      let maxHeight = 0;
      const dragInfo = dragStore?.draggedElements ?? [];
      const clones: HTMLElement[] = [];
      for (const info of dragInfo) {
        const el = document.querySelector(`[data-drag-id='${info.dragID}']`);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.left < minLeft) minLeft = rect.left;
        if (rect.top < minTop) minTop = rect.top;
        if (rect.width > maxWidth) maxWidth = rect.width;
        if (rect.height > maxHeight) maxHeight = rect.height;
        //console.log(">>> CLONE", el);
        const clone = el.cloneNode(true) as HTMLElement;
        clone.style.marginLeft = "0px";
        clones.push(clone)
      }
      setClones(clones);
      //console.log(">>> SETPOS", minLeft, minTop);
      setPos({ x: minLeft, y: minTop });
      setSize({ x: maxWidth, y: maxHeight });
    } else {
      dragStore?.setDrag(false);
      setMousePos(undefined);
      dragStore?.initDragMouse(undefined, undefined);
      setClones([]);
    }
  }, [isReadyToDrag]);

  const onMouseMove = useCallback((ev: MouseEvent) => {
    //console.log(">>> MOUSE POS", mousePos);
    if (!mousePos) {
      const mpos = { x: ev.clientX, y: ev.clientY };
      setMousePos(mpos);
      mousePosRef.current = mpos;
      dragStore?.initDragMouse(mpos, { x: 0, y: 0 });
    } else {
      mousePosRef.current = { x: ev.clientX, y: ev.clientY };
      autoScroll(ev.clientY);
      dragStore?.moveDragMouse({ x: ev.clientX - mousePos.x, y: ev.clientY - mousePos.y }, mousePos);
    };
  }, [autoScroll, mousePos]);

  useEffect(() => {
    if (!isReadyToDrag) return;
    const timer = window.setInterval(() => autoScroll(mousePosRef.current?.y), AUTO_SCROLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [autoScroll, isReadyToDrag]);

  useEffect(() => {
    if (!placeStore || (!isReadyToDrag && !isDrag && dragElements.length < 1)) return;

    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key !== "Escape") return;
      setReadyToDrag(false);
      setMousePos(undefined);
      mousePosRef.current = undefined;
      placeStore.clearDrag();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [dragElements.length, isDrag, isReadyToDrag, placeStore]);

  useEffect(() => {
    if (isReadyToDrag) {
      if (onMouseMoveRef.current) {
        window.removeEventListener("mousemove", onMouseMoveRef.current);
      }
      window.addEventListener("mousemove", onMouseMove);
      onMouseMoveRef.current = onMouseMove;
    } else if (onMouseMoveRef.current) {
      window.removeEventListener("mousemove", onMouseMoveRef.current);
      onMouseMoveRef.current = null;
    }
    return () => {
      if (onMouseMoveRef.current) window.removeEventListener("mousemove", onMouseMoveRef.current);
    }
  }, [isReadyToDrag, onMouseMove]);

  useEffect(() => {
    const hasDraggedElements = (dragElements?.length ?? 0) > 0;

    if (hasDraggedElements && !hadDraggedElements) {
      const onMouseDown = (ev: MouseEvent) => {
        if (ev.ctrlKey) return;
        const dragInfo = dragStore?.draggedElements ?? [];
        for (const info of dragInfo) {
          const el = document.querySelector(`[data-drag-id='${info.dragID}']`);
          //console.log(">>> MOUSEDOWN", dragInfo, el);
          if (!el) continue;
          const rect = el.getBoundingClientRect();
          //console.log(">>> SETREADY", el, rect, ev.clientX, ev.clientY);
          if (ev.clientX > rect.left && ev.clientX < rect.right && ev.clientY > rect.top && ev.clientY < rect.bottom) {
            setReadyToDrag(true);
            break;
          }
        }
      };
      const onMouseUp = (ev: MouseEvent) => {
        setReadyToDrag(false);
      };
      document.addEventListener("mousedown", onMouseDown, true);
      document.addEventListener("mouseup", onMouseUp, true);
      return () => {
        document.removeEventListener("mousedown", onMouseDown, true);
        document.removeEventListener("mouseup", onMouseUp, true);
      }
    }

    setHadDraggedElements(hasDraggedElements);
  }, [dragElements]);

  return {
    pos, size, clones, isDrag,
  }
}
